function normalizeObjectArray(input) {
  if (!Array.isArray(input)) return [];
  return input.filter((item) => item && typeof item === "object");
}

export function normalizeTemplateId(templateId) {
  if (!templateId) return 'harvard-classic';

  // Step 1: Normalize to kebab-case.
  // Handles both gallery input ('startup-neon') and legacy DB values ('startup_neon').
  const clean = templateId.trim().toLowerCase().replace(/_/g, '-');

  // Step 2: Legacy aliases only — for IDs that predate the kebab registry entirely.
  // These are single-word IDs from the original launch (pre-2025).
  // All 37 current template IDs are already kebab and do not need this map.
  const LEGACY_ALIASES = {
    'tech':         'harvard-classic',
    'classic':      'harvard-classic',
    'nexus':        'harvard-classic',
    'mba':          'modern-ats-pro',
    'cleanslate':   'modern-ats-pro',
    'apex':         'modern-ats-pro',
    'elegant':      'modern-ats-pro',
    'minimal':      'modern-ats-pro',
    'timeline':     'campus-placement',
    'finpro':       'finance-pro',
    'finpro2col':   'finance-pro',
    'professional': 'finance-pro',
  };

  // Step 3: Return alias if matched, otherwise return the kebab-normalized ID.
  // Unknown IDs are passed through — the registry will surface a clear error
  // rather than silently rendering Harvard Classic.
  return LEGACY_ALIASES[clean] ?? clean;
}



export default function normalizeResumeData(rawData) {
  if (!rawData || typeof rawData !== "object") return null;

  const pendingName = typeof window !== 'undefined' ? localStorage.getItem('pending_scratch_name') : null;
  const pendingRole = typeof window !== 'undefined' ? localStorage.getItem('pending_scratch_role') : null;

  return {
    ...rawData,
    name: rawData.name || (pendingName ? (localStorage.removeItem('pending_scratch_name'), pendingName) : ""),
    email: rawData.email || "",
    phone: rawData.phone || "",
    city: rawData.city || "",
    role: rawData.role || (pendingRole ? (localStorage.removeItem('pending_scratch_role'), pendingRole) : ""),
    summary: rawData.summary || "",
    skills: Array.isArray(rawData.skills) ? rawData.skills.filter((skill) => typeof skill === "string" && skill.trim()) : [],
    experience: normalizeObjectArray(rawData.experience),
    education: normalizeObjectArray(rawData.education),
    internships: normalizeObjectArray(rawData.internships),
    certifications: Array.isArray(rawData.certifications) ? rawData.certifications : [],
    extracurricular: Array.isArray(rawData.extracurricular) ? rawData.extracurricular : [],
    interests: Array.isArray(rawData.interests) ? rawData.interests : (rawData.interests ? [rawData.interests] : []),
    hobbies: Array.isArray(rawData.hobbies) ? rawData.hobbies : (rawData.hobbies ? [rawData.hobbies] : []),
    achievements: Array.isArray(rawData.achievements) ? rawData.achievements : [],
    projects: normalizeObjectArray(rawData.projects),
    links: rawData.links && typeof rawData.links === "object" ? rawData.links : {},
    languages: Array.isArray(rawData.languages) ? rawData.languages.filter((l) => typeof l === "string" && l.trim()) : [],
    photo_url: rawData.photo_url || "",
    // Domain metadata (set via domain selector, null = not yet selected)
    _domain: rawData._domain || null,
    _section_order: Array.isArray(rawData._section_order) ? rawData._section_order : (rawData.section_order ? rawData.section_order : null),
    _experience_level: rawData._experience_level || null,
    _template: normalizeTemplateId(rawData._template),
    // section_order for templates (they read resumeData.section_order)
    section_order: Array.isArray(rawData.section_order) ? rawData.section_order : (Array.isArray(rawData._section_order) ? rawData._section_order : null),
  };
}


export function getCompletionStatus(data) {
  if (!data) return [];
  
  // Contact: name is NOT enough — must have email or phone too
  const hasContact = Boolean(data.name) && Boolean(data.email || data.phone);
  const hasRole = Boolean(data.role);
  const hasSkills = data.skills?.length >= 5;
  // Experience: real work/internship OR at least 1 project (freshers)
  const hasWork = (data.experience?.length > 0) || (data.internships?.length > 0);
  const hasProjects = data.projects?.length > 0;
  const hasExperienceOrProjects = hasWork || hasProjects;
  const hasEducation = data.education?.length > 0;

  return [
    { key: "contact",    label: "Contact",           done: hasContact,              message: "Add my phone number and email" },
    { key: "role",       label: "Role",               done: hasRole,                 message: "Let's set my target role" },
    { key: "skills",     label: "Skills",             done: hasSkills,               message: "I want to add some skills" },
    { key: "experience", label: hasWork ? "Experience" : "Projects", done: hasExperienceOrProjects, message: hasWork ? "Let's add my work experience" : "Let's add a project I built" },
    { key: "education",  label: "Education",          done: hasEducation,            message: "Let's add my education" },
  ];
}
